/**
 * CRS Detail Page - single readiness score with radar chart, gaps, roadmap.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  CheckCircle2,
  Gauge,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import capitalReadinessService from '../services/capitalReadinessService';
import { CapitalReadinessScore, GapSeverity } from '../types/capitalReadiness';
import CRSTierBadge, { tierColorClasses } from '../components/CRSTierBadge';
import CRSRadarChart from '../components/CRSRadarChart';
import { scoreColor } from './CRSDashboardPage';

function severityBadge(sev: GapSeverity | string): string {
  switch (sev) {
    case 'critical':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'high':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'medium':
      return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'low':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
}

const formatDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString() : '—';

const CRSDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const scoreId = Number(id);

  const [score, setScore] = useState<CapitalReadinessScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);

  const load = async () => {
    if (!scoreId) {
      toast.error('Invalid score id');
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await capitalReadinessService.fetchScore(scoreId);
    if (res.error) {
      toast.error(res.error);
    } else if (res.data) {
      setScore(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreId]);

  const handleRescore = async () => {
    if (!score) return;
    setRescoring(true);
    const res = await capitalReadinessService.rescore(score.id);
    setRescoring(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setScore(res.data);
      toast.success('Rescored');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading score...
        </div>
      </DashboardLayout>
    );
  }

  if (!score) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <p className="mb-4">Score not found.</p>
          <Link to="/capital-readiness" className="text-blue-600 hover:underline">
            Back to readiness
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const entityName =
    score.project?.project_name ||
    score.startup?.name ||
    (score.project_id
      ? `Project #${score.project_id}`
      : score.startup_id
      ? `Startup #${score.startup_id}`
      : '—');

  const gaps = score.gaps || [];
  const roadmap = score.improvement_roadmap || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/capital-readiness"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to capital readiness
        </Link>

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Gauge className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{entityName}</h1>
                <p className="text-sm text-gray-600 mt-0.5">
                  Scored {formatDate(score.created_at)}
                  {score.version != null && ` · v${score.version}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CRSTierBadge tier={score.tier} />
              <button
                onClick={handleRescore}
                disabled={rescoring}
                className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50"
              >
                {rescoring ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                )}
                Rescore
              </button>
            </div>
          </div>

          {/* Score + radar */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="flex flex-col items-center justify-center">
              <ScoreCircle score={score.overall_score} tier={score.tier} />
              <p className="text-xs text-gray-500 mt-3">Overall score</p>
            </div>
            <div className="flex items-center justify-center">
              <CRSRadarChart scores={score.dimension_scores || {}} size={300} />
            </div>
          </div>
        </div>

        {/* Gaps */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
            Gaps ({gaps.length})
          </h3>
          {gaps.length === 0 ? (
            <p className="text-sm text-gray-500 inline-flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              No gaps identified.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {gaps.map((g, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900 capitalize">
                      {String(g.dimension).replace(/_/g, ' ')}
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium border capitalize ${severityBadge(
                        g.severity
                      )}`}
                    >
                      {g.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{g.description}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Improvement Roadmap */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Improvement roadmap ({roadmap.length})
          </h3>
          {roadmap.length === 0 ? (
            <p className="text-sm text-gray-500">No improvement steps.</p>
          ) : (
            <ol className="space-y-4">
              {roadmap.map((step, idx) => (
                <li key={idx} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                    {step.priority ?? idx + 1}
                  </div>
                  <div className="flex-1 border-l-2 border-gray-200 pl-4 pb-2">
                    <p className="text-sm text-gray-900 font-medium">
                      {step.step}
                    </p>
                    {step.dimension && (
                      <p className="text-xs text-gray-500 mt-0.5 capitalize">
                        {String(step.dimension).replace(/_/g, ' ')}
                      </p>
                    )}
                    {step.recommended_providers &&
                      step.recommended_providers.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {step.recommended_providers.map((p, pidx) => {
                            const content = (
                              <>
                                <span>{p.name}</span>
                                {p.provider_type && (
                                  <span className="text-gray-500">
                                    {' · '}
                                    {String(p.provider_type).replace(/_/g, ' ')}
                                  </span>
                                )}
                                {p.website_url && (
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                )}
                              </>
                            );
                            return p.website_url ? (
                              <a
                                key={pidx}
                                href={p.website_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center px-2 py-1 border border-blue-300 text-blue-700 rounded text-xs hover:bg-blue-50 gap-1"
                              >
                                {content}
                              </a>
                            ) : (
                              <span
                                key={pidx}
                                className="inline-flex items-center px-2 py-1 border border-gray-300 text-gray-700 rounded text-xs gap-1"
                              >
                                {content}
                              </span>
                            );
                          })}
                        </div>
                      )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* History link */}
        <div className="text-sm">
          <Link
            to={`/capital-readiness/history?${
              score.startup_id
                ? `startup_id=${score.startup_id}`
                : `project_id=${score.project_id}`
            }`}
            className="text-blue-600 hover:underline"
          >
            View score history for this entity →
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

const ScoreCircle: React.FC<{ score: number; tier: string }> = ({
  score,
  tier,
}) => {
  const radius = 72;
  const stroke = 12;
  const norm = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const offset = norm * (1 - pct);

  const strokeColor =
    score >= 80
      ? '#16A34A'
      : score >= 65
      ? '#059669'
      : score >= 50
      ? '#D97706'
      : score >= 35
      ? '#EA580C'
      : '#DC2626';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={radius * 2 + stroke * 2} height={radius * 2 + stroke * 2}>
        <circle
          cx={radius + stroke}
          cy={radius + stroke}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={stroke}
        />
        <circle
          cx={radius + stroke}
          cy={radius + stroke}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={norm}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${radius + stroke} ${radius + stroke})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-bold ${scoreColor(score)}`}>
          {Math.round(score)}
        </span>
        <span
          className={`mt-1 px-2 py-0.5 rounded-full border text-[10px] font-medium capitalize ${tierColorClasses(
            tier
          )}`}
        >
          {tier.replace(/_/g, ' ')}
        </span>
      </div>
    </div>
  );
};

export default CRSDetailPage;
